/**
 * Capa HTTP del widget de feedback.
 *
 * `APIClient` NO sirve para el envío: serializa el body con `JSON.stringify`, y esto es multipart
 * (N imágenes + 1 audio). Los headers viven en `utils/multipart.js` (compartidos con el chat de
 * soporte y la subida de archivos a la Memoria del cliente); la normalización de errores sigue
 * siendo local a propósito: este panel resuelve el copy por `error_code` y nunca muestra el
 * `error_message` del backend.
 */
import * as Sentry from "@sentry/react";

import {API_URL} from "../../settings/local_settings";
import {APIClient} from "../../utils/APIClient";
import {buildMultipartHeaders} from "../../utils/multipart";

const NETWORK_ERROR_RESPONSE = {
    status: "NETWORK_ERROR",
    error_code: "NETWORK_ERROR",
    error_message: "",
};

/**
 * Arma el FormData del envío.
 *
 * Las N capturas van con el MISMO nombre de campo repetido (`screenshots`), que es lo que
 * `request.FILES.getlist('screenshots')` espera en Django. Es el punto donde es tentador escribir
 * `screenshots[0]` / `screenshots_0` y romper el contrato en silencio: el backend recibiría cero
 * imágenes y nadie se enteraría. Hay un test que lo cubre.
 */
export function buildFeedbackFormData({type, text, aiResponse, promptVersion, includeContext,
                                       context, attachments, audio, dedupKey}) {
    const formData = new FormData();
    formData.append("type", type || "");
    formData.append("message", text || "");
    if (aiResponse) formData.append("ia_response", aiResponse);
    if (promptVersion) formData.append("prompt_version", promptVersion);
    if (dedupKey) formData.append("dedup_key", dedupKey);

    // Si el usuario destildó el checkbox, la key NO viaja. No es lo mismo que mandar un objeto
    // vacío: el checkbox dice que no se envía, y tiene que ser verdad.
    if (includeContext && context) {
        formData.append("context", JSON.stringify(context));
    }

    (attachments || []).forEach((attachment) => {
        formData.append("screenshots", attachment.blob, attachment.filename);
    });

    if (audio && audio.blob) {
        formData.append("audio", audio.blob, audio.filename);
        if (audio.durationSeconds) {
            formData.append("audio_duration", String(audio.durationSeconds));
        }
    }

    return formData;
}

async function parseError(response) {
    if (response.status === 429) {
        return {status: "ERROR", error_code: "RATE_LIMITED", error_message: ""};
    }
    if (response.status === 413) {
        return {status: "ERROR", error_code: "TOO_LARGE", error_message: ""};
    }
    try {
        const body = await response.json();
        return {
            status: "ERROR",
            error_code: body.error_code || "",
            // El mensaje del backend NO se muestra crudo (regla cross-agency): el panel elige el
            // copy por `error_code`. Se conserva sólo para Sentry.
            error_message: body.error_message || "",
        };
    } catch (_) {
        return {status: "ERROR", error_code: "", error_message: ""};
    }
}

/** Nunca tira: siempre devuelve `{status, error_code, error_message}`. */
export async function submitFeedback(payload) {
    try {
        const response = await fetch(API_URL + "feedback/", {
            method: "POST",
            headers: buildMultipartHeaders(),
            body: buildFeedbackFormData(payload),
        });
        if (!response.ok) return await parseError(response);
        const body = await response.json();
        return {status: "OK", feedback: body.feedback};
    } catch (error) {
        Sentry.captureException(error);
        return {...NETWORK_ERROR_RESPONSE};
    }
}

/**
 * Config remota del widget: el prompt del flujo `ia_bug` y el destino de las consultas.
 *
 * Vive en el backend para que se pueda cambiar sin un deploy del frontend. Va con `{cache: true}`
 * (TTL 30s de APIClient) porque varios abren y cierran el panel en la misma sesión.
 */
export async function fetchFeedbackConfig() {
    return new APIClient(API_URL + "feedback/config/").call("GET", {}, {cache: true});
}
